import {
  FulfillmentGroupData,
  getFulfillmentGroupValue,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  DEFAULT_INSURANCE_INCLUDED_THRESHOLD,
  fulfillmentGroupInsuranceEligible,
  getInsuranceAmountIncludedByDefaultThreshold,
  getParcelInsuranceCost,
  NO_INSURANCE_REASON,
  PARCEL_THRESHOLD_REASON,
} from "@redotech/redo-model/outbound-labels/fulfillment-group-insurance";
import { OutboundParcel } from "@redotech/redo-model/outbound-labels/parcel";
import { Team } from "@redotech/redo-model/team";

type InsuranceUiSelection =
  | "fullCoverage"
  | "customCoverage"
  | "defaultCoverage";

export function useInsuranceInfo({
  parcelBeingEditedPerGroup,
  groups,
  team,
}: {
  parcelBeingEditedPerGroup: readonly OutboundParcel[];
  groups: readonly FulfillmentGroupData[];
  team: Team;
}):
  | { canEdit: false; reason: NO_INSURANCE_REASON }
  | {
      canEdit: true;
      isBulkEdit: boolean;
      mixedCustomInsurance: boolean;
      insuredAmount: number | null;
      totalFullCoverageInsuranceCost: number;
      customInsuranceDisplayedCost: number;
      insuranceIncludedThreshold: number;
      groupCanBeFullyInsured: boolean;
      insuranceCoverageReason: PARCEL_THRESHOLD_REASON;
      insuranceUiSelection: InsuranceUiSelection;
    } {
  if (groups.length === 0 || parcelBeingEditedPerGroup.length === 0) {
    return {
      canEdit: false,
      reason: NO_INSURANCE_REASON.BULK_EDIT_NOT_ALLOWED,
    };
  }

  // All groups must be eligible for insurance
  for (const group of groups) {
    const eligibility = fulfillmentGroupInsuranceEligible({
      team,
      fulfillmentGroup: group,
    });
    if (!eligibility.insuranceEligible) {
      return { canEdit: false, reason: eligibility.reason };
    }
  }

  const insuranceRatePercentage =
    team?.settings?.outboundLabels?.insuranceRatePercentage || 1;
  const teamInsuranceIncludedThreshold =
    team?.settings?.outboundLabels?.insuranceIncludedThreshold ??
    DEFAULT_INSURANCE_INCLUDED_THRESHOLD;

  // Compute the threshold for each group/parcel pair and verify they all match
  const thresholdResults = groups.map((group, i) => {
    const parcel = parcelBeingEditedPerGroup[i];
    return getInsuranceAmountIncludedByDefaultThreshold({
      carrier: group.selectedShippingRate?.carrier ?? null,
      service: group.selectedShippingRate?.service ?? null,
      teamInsuranceIncludedThreshold,
      packageWeight: parcel?.parcelAndContentWeight ?? null,
    });
  });

  const firstResult = thresholdResults[0];
  if (!firstResult) {
    return { canEdit: false, reason: NO_INSURANCE_REASON.NO_PARCELS_SELECTED };
  }

  const allThresholdsSame = thresholdResults.every(
    (r) => r.parcelInsuranceThreshold === firstResult.parcelInsuranceThreshold,
  );
  if (!allThresholdsSame) {
    return {
      canEdit: false,
      reason: NO_INSURANCE_REASON.BULK_EDIT_NOT_ALLOWED,
    };
  }

  const parcelInsuranceThreshold = firstResult.parcelInsuranceThreshold;
  const reason = firstResult.reason;

  // Aggregate costs across all groups and determine UI selection
  let totalFullCoverageInsuranceCost = 0;
  let totalCustomInsuranceDisplayedCost = 0;
  let allGroupsCanBeFullyInsured = true;
  let allDefault = true;
  let allFullCoverage = true;
  let mixedCustomInsurance = false;
  let firstCustomInsuredAmount: number | undefined;

  for (const [i, group] of groups.entries()) {
    const parcel = parcelBeingEditedPerGroup[i];
    const groupValue = getFulfillmentGroupValue(group);

    totalFullCoverageInsuranceCost += getParcelInsuranceCost({
      teamRatePercentage: insuranceRatePercentage,
      valueToInsure: groupValue,
      parcelInsuranceThreshold,
    });

    const parcelInsuredAmount = parcel?.insurance?.insuredAmount;
    if (parcelInsuredAmount != null) {
      totalCustomInsuranceDisplayedCost += getParcelInsuranceCost({
        teamRatePercentage: insuranceRatePercentage,
        valueToInsure: parcelInsuredAmount,
        parcelInsuranceThreshold,
      });
    }
    if (groupValue < parcelInsuranceThreshold) {
      allGroupsCanBeFullyInsured = false;
    }
    if (
      parcelInsuredAmount != null &&
      parcelInsuredAmount >= parcelInsuranceThreshold
    ) {
      allDefault = false;
    }

    if (parcelInsuredAmount !== groupValue) {
      allFullCoverage = false;
    }

    if (firstCustomInsuredAmount === undefined) {
      firstCustomInsuredAmount = parcelInsuredAmount ?? 0;
    } else if (parcelInsuredAmount !== firstCustomInsuredAmount) {
      mixedCustomInsurance = true;
    }
  }

  const firstParcel = parcelBeingEditedPerGroup[0];
  const insuredAmount = firstParcel?.insurance?.insuredAmount ?? null;

  let insuranceUiSelection: InsuranceUiSelection;
  if (allFullCoverage) {
    insuranceUiSelection = "fullCoverage";
  } else if (allDefault || mixedCustomInsurance) {
    insuranceUiSelection = "defaultCoverage";
  } else {
    insuranceUiSelection = "customCoverage";
  }

  return {
    canEdit: true,
    isBulkEdit: groups.length > 1,
    mixedCustomInsurance,
    insuredAmount,
    totalFullCoverageInsuranceCost,
    insuranceUiSelection,
    insuranceIncludedThreshold: parcelInsuranceThreshold,
    groupCanBeFullyInsured: allGroupsCanBeFullyInsured,
    insuranceCoverageReason: reason,
    customInsuranceDisplayedCost: totalCustomInsuranceDisplayedCost,
  };
}
